/* Copyright (c) Trimble Inc. All rights reserved. */

import { TokenValidationHelper } from './TokenValidator.helper';
import { isOAuthCallback } from '../utils/urlBuilder.util';
import { useTokenStore } from '../stores/Token.store';
import AuthRedrectionHelper from './AuthRedirector.helper';
import { AuthCheckResult } from '../models/AuthChecker.model';
import { Logger, Persistor } from '@tcp-base/toolkit';
import 'reflect-metadata';
import { AUTH_REDIRECT_PATH_STORAGE_KEY, OAUTH_CALLBACK_PARAMS } from '../config/CONSTANTS';
/**
 * Auth Checker Service
 *
 * Pure functions for checking authentication state and determining appropriate actions.
 * Does not manage state directly - returns results for components to handle.
 */

class AuthCheckerImpl {
  /**
   * Check authentication state and return action recommendations
   *
   * Pure async function that:
   * - Checks for OAuth callback state
   * - Validates access tokens from IndexedDB
   * - Determines if redirect is needed
   * - Returns structured result for state management
   */
  checkAuthenticationState = async (): Promise<AuthCheckResult> => {
    try {
      // Check if we're in an OAuth callback flow (?code= still in URL)
      if (isOAuthCallback()) {
        return {
          shouldSkip: true,
          tokenValid: false,
          token: null,
          shouldRedirect: false,
          error: null,
          reason: 'oauth_callback',
        };
      }

      // Also skip when TokenExchanger has already read the code and is mid-exchange.
      // The URL may have been cleaned before the token is persisted; the sessionStorage
      // flag bridges that gap so we never trigger a login redirect mid-exchange.
      try {
        if (sessionStorage.getItem('OAUTH_EXCHANGE_IN_PROGRESS') === 'true') {
          return {
            shouldSkip: true,
            tokenValid: false,
            token: null,
            shouldRedirect: false,
            error: null,
            reason: 'oauth_callback',
          };
        }
      } catch {
        // sessionStorage not available — ignore and continue
      }

      // Validate the current access token from IndexedDB
      const validation = await TokenValidationHelper.validateAccessToken();

      if (validation.valid && validation.token) {
        // Token is valid - check if there's a stored redirect path from before login
        this.handlePostLoginRedirect();

        return {
          shouldSkip: false,
          tokenValid: true,
          token: validation.token,
          shouldRedirect: false,
          error: null,
          reason: 'token_valid',
        };
      } else {
        // Token is invalid or expired

        // Clean up expired token if it exists
        if (validation.exists && validation.expired && validation.token) {
          useTokenStore.getState()?.removeTokenDetails();
        }

        // Check if we can redirect to login
        const canRedirect = AuthRedrectionHelper.isConfigured();

        if (!canRedirect) {
          return {
            shouldSkip: false,
            tokenValid: false,
            token: null,
            shouldRedirect: false,
            error: 'AuthService not configured. Cannot redirect to login.',
            reason: 'auth_not_configured',
          };
        }

        // Determine the reason for invalid token
        const reason = validation.exists && validation.expired ? 'token_expired' : 'token_invalid';

        return {
          shouldSkip: false,
          tokenValid: false,
          token: null,
          shouldRedirect: true,
          error: null,
          reason,
        };
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to check authentication state';
      Logger.error('AuthChecker: Token validation error:', error);

      const canRedirect = AuthRedrectionHelper.isConfigured();

      return {
        shouldSkip: false,
        tokenValid: false,
        token: null,
        shouldRedirect: canRedirect,
        error: errorMessage,
        reason: 'validation_error',
      };
    }
  };

  /**
   * Perform login redirect if authentication state requires it
   *
   * Separated from state checking to maintain pure function principles
   * Now handles async PKCE generation during login
   */
  //@LogErrors()
  public async performLoginRedirect(): Promise<void> {
    try {
      if (AuthRedrectionHelper.isConfigured()) {
        await AuthRedrectionHelper.login();
      } else {
        Logger.error('AuthChecker: Cannot redirect - AuthService not configured');
      }
    } catch (error) {
      Logger.error('AuthChecker: Login redirect failed:', error);
    }
  }

  /**
   * Handle post-login redirect to stored path
   *
   * After successful authentication, checks if there's a stored redirect path
   * from before login and navigates to it. The path is then cleared from storage
   * to prevent repeated redirects.
   *
   * Flow:
   * 1. User visits /some/protected/page
   * 2. No token -> save /some/protected/page -> redirect to login
   * 3. After login success -> token valid -> redirect back to /some/protected/page
   */
  public handlePostLoginRedirect(): void {
    try {
      // Get stored redirect path from sessionStorage
      const storedPath = Persistor.getItem<string>(AUTH_REDIRECT_PATH_STORAGE_KEY);

      if (storedPath && typeof storedPath === 'string') {
        // Get current path to compare
        const currentPath =
          window.location.pathname + window.location.search + window.location.hash;

        // Only redirect if we're not already on the stored path
        // and the stored path is different from current location
        if (storedPath !== currentPath && storedPath !== '/') {
          Logger.debug('AuthChecker: Redirecting to stored path:', storedPath);

          // Clear the stored path to prevent repeated redirects
          Persistor.removeItem(AUTH_REDIRECT_PATH_STORAGE_KEY);

          // Strip any OAuth params that may have been persisted in the stored path
          // (defensive: e.g. if the path was saved during a callback URL visit)
          let redirectPath = storedPath;
          try {
            const cleanUrl = new URL(storedPath, window.location.origin);
            OAUTH_CALLBACK_PARAMS.forEach((p) => cleanUrl.searchParams.delete(p));
            redirectPath = cleanUrl.pathname + cleanUrl.search + cleanUrl.hash;
          } catch {
            // Fallback: strip everything after '?' to be safe
            redirectPath = storedPath.split('?')[0];
          }

          // Navigate to the stored path
          window.location.replace(redirectPath);
        } else {
          // Already on the correct path, just clear storage
          Persistor.removeItem(AUTH_REDIRECT_PATH_STORAGE_KEY);
        }
      }
    } catch (error) {
      Logger.warn('AuthChecker: Failed to handle post-login redirect:', error);
      // Clear storage on error to prevent issues
      try {
        Persistor.removeItem(AUTH_REDIRECT_PATH_STORAGE_KEY);
      } catch {
        // Ignore cleanup errors
      }
    }
  }

  /**
   * Get human-readable description of check result
   */
  getCheckResultDescription = (result: AuthCheckResult): string => {
    switch (result.reason) {
      case 'oauth_callback':
        return 'OAuth callback in progress, skipping auth check';
      case 'token_valid':
        return 'Access token is valid';
      case 'token_invalid':
        return 'No access token found';
      case 'token_expired':
        return 'Access token has expired';
      case 'validation_error':
        return `Token validation failed: ${result.error}`;
      case 'auth_not_configured':
        return 'Authentication service not configured';
      default:
        return 'Unknown authentication state';
    }
  };
}

export const AuthCheckHelper = new AuthCheckerImpl();
